import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { useCart } from './cart/CartContext';
import { useCartStockSync } from './cart/useCartStockSync';
import { lineMaxQty } from './cart/cartUtils';
import PublicImage from './PublicImage';
import CheckoutProgress from './checkout/CheckoutProgress';
import OrderSummaryPanel from './checkout/OrderSummaryPanel';
import QtyStepper from './checkout/QtyStepper';
import StoreTrustBadges from './checkout/StoreTrustBadges';
import { formatINR } from './checkout/formatINR';
import { productDetailPath } from './productPaths';
import OrderCustomizationDisplay from './jewelry/OrderCustomizationDisplay';
import { cartLineKey } from './cart/cartUtils';
import ProductRefId from './catalog/ProductRefId';
import { readRecentOrderSuccess } from './checkout/orderSuccessStorage';
import { useSiteSettings } from './SiteSettingsContext';
import WishlistCartNudge from './components/WishlistCartNudge';

export default function CartPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ready, isAuthed } = useAuth();
  const { settings: site } = useSiteSettings();
  const store = site.store_copy || {};
  const { lines, subtotal, setQty, removeItem } = useCart();
  const { stockNotice } = useCartStockSync();
  const [shippingFee, setShippingFee] = useState(0);
  const [shippingLoading, setShippingLoading] = useState(true);

  useEffect(() => {
    fetch('/api/checkout_config.php')
      .then((r) => r.json())
      .then((d) => {
        if (d?.ok) setShippingFee(Number(d.shipping_flat_inr) || 0);
      })
      .catch(() => {})
      .finally(() => setShippingLoading(false));
  }, []);

  const itemCount = useMemo(() => lines.reduce((s, l) => s + l.qty, 0), [lines]);
  const checkoutReason = searchParams.get('reason') || '';
  const checkoutInterrupted = checkoutReason === 'checkout-empty';
  const stockCleared = checkoutReason === 'stock-cleared';

  const goToCheckout = () => {
    if (!ready || lines.length === 0) return;
    navigate('/checkout');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (searchParams.get('reason') !== 'checkout-empty') return;
    const recent = readRecentOrderSuccess();
    if (recent?.orderNumber) {
      navigate(`/order-success/${encodeURIComponent(recent.orderNumber)}`, { replace: true });
    }
  }, [searchParams, navigate]);

  return (
    <div className="store-page cart-page">
      <Helmet>
        <title>{store.seo_cart_title}</title>
        {store.seo_cart_description ? <meta name="description" content={store.seo_cart_description} /> : null}
      </Helmet>

      <div className="container store-page__container">
        <header className="store-page__header cart-page__header">
          <CheckoutProgress current="cart" />
          <div className="cart-page__title-row">
            <div>
              <h1 className="store-page__title">{store.cart_title}</h1>
              <p className="store-page__lead mb-0">
                {lines.length === 0
                  ? store.cart_empty_lead_default
                  : store.cart_nonempty_lead}
              </p>
            </div>
            {lines.length > 0 ? (
              <span className="cart-page__badge" aria-label={`${itemCount} items in bag`}>
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </span>
            ) : null}
          </div>
        </header>

        {stockNotice ? (
          <div className="alert alert-warning py-2 small mb-3" role="status">
            {stockNotice}
          </div>
        ) : null}

        <WishlistCartNudge placement="cart" />

        {lines.length === 0 ? (
          <div className="store-empty cart-empty">
            <div className="store-empty__icon cart-empty__icon" aria-hidden>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6 6h15l-1.5 9h-12L6 6zm0 0L5 3H2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="9" cy="20" r="1.25" fill="currentColor" />
                <circle cx="17" cy="20" r="1.25" fill="currentColor" />
              </svg>
            </div>
            <h2 className="store-empty__title">{store.cart_empty_title}</h2>
            <p className="store-empty__text">
              {stockCleared
                ? store.cart_empty_lead_stock
                : checkoutInterrupted
                  ? store.cart_empty_lead_checkout
                  : store.cart_empty_lead_default}
            </p>
            {checkoutInterrupted || stockCleared ? (
              <div className="d-flex flex-wrap gap-2 justify-content-center mt-2">
                <Link to="/account" className="btn btn-outline-primary rounded-pill">
                  My account
                </Link>
                <Link to="/track-order" className="btn btn-outline-secondary rounded-pill">
                  Track order
                </Link>
              </div>
            ) : null}
            <Link to="/shop" className="btn btn-primary btn-lg rounded-pill store-cta">
              Shop collections
            </Link>
          </div>
        ) : (
          <>
            <div className="store-layout cart-layout">
              <section className="store-layout__main cart-bag" aria-label="Cart items">
                <ul className="cart-line-list list-unstyled">
                  {lines.map((line) => {
                    const maxQty = lineMaxQty(line.stockQty);
                    const tracked = line.stockQty !== null && line.stockQty !== undefined;
                    return (
                      <li
                        key={cartLineKey(line.categorySlug, line.productId, line.customization)}
                        className="cart-line"
                      >
                        <div className="cart-line__media">
                          {line.image ? (
                            <PublicImage src={line.image} alt="" className="cart-line__img" />
                          ) : (
                            <div className="cart-line__img cart-line__img--empty" aria-hidden />
                          )}
                        </div>

                        <div className="cart-line__body">
                          <div className="cart-line__top">
                            <div className="cart-line__top-main">
                              <h2 className="cart-line__title">
                                <Link
                                  to={productDetailPath({
                                    category: line.categorySlug,
                                    id: line.productId,
                                  })}
                                  className="text-decoration-none text-body"
                                >
                                  {line.name}
                                </Link>
                              </h2>
                              <ProductRefId id={line.productId} className="product-id small mb-1" />
                              {line.customization ? (
                                <p className="cart-line__personalization small mb-1">
                                  <OrderCustomizationDisplay raw={line.customization} />
                                </p>
                              ) : null}
                              <p className="cart-line__meta">
                                <span className="cart-line__unit">{formatINR(line.price)} each</span>
                                {tracked && maxQty < 99 ? (
                                  <span className="cart-line__stock small text-muted d-block">
                                    {maxQty} in stock
                                  </span>
                                ) : null}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="cart-line__remove"
                              onClick={() =>
                                removeItem(line.categorySlug, line.productId, line.customization)
                              }
                            >
                              Remove
                            </button>
                          </div>

                          <div className="cart-line__controls">
                            <div className="cart-line__qty">
                              <span className="cart-line__qty-label" id={`qty-label-${line.productId}`}>
                                Quantity
                              </span>
                              <QtyStepper
                                size="md"
                                value={line.qty}
                                min={1}
                                max={maxQty}
                                onChange={(n) =>
                                  setQty(line.categorySlug, line.productId, n, line.customization)
                                }
                                label={`Quantity for ${line.name}`}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="cart-line__aside">
                          <span className="cart-line__total-label">Line total</span>
                          <span className="cart-line__total">{formatINR(line.price * line.qty)}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <Link to="/shop" className="cart-continue-shop">
                  <span aria-hidden>←</span> Continue shopping
                </Link>
              </section>

              <div className="store-layout__aside store-layout__aside--sticky cart-summary-col">
                <OrderSummaryPanel
                  lines={lines}
                  subtotal={subtotal}
                  shipping={shippingFee}
                  shippingLoading={shippingLoading}
                  editCartHref={null}
                  primaryAction={
                    <button
                      type="button"
                      className="btn btn-primary btn-lg rounded-pill w-100 store-cta"
                      onClick={goToCheckout}
                    >
                      Proceed to checkout
                    </button>
                  }
                  footnote={
                    isAuthed
                      ? 'Delivery fee shown above is applied at checkout for orders within India.'
                      : 'You can check out as a guest — sign in is optional.'
                  }
                />
                {!isAuthed ? (
                  <p className="small text-muted text-center mt-2 mb-0">
                    <Link to={`/login?next=${encodeURIComponent('/checkout')}`}>Sign in</Link> for saved
                    addresses and order history.
                  </p>
                ) : null}
                <StoreTrustBadges />
              </div>
            </div>

            <div className="cart-mobile-bar d-lg-none" role="region" aria-label="Checkout">
              <div className="cart-mobile-bar__total">
                <span className="cart-mobile-bar__label">Subtotal</span>
                <strong className="cart-mobile-bar__amount">{formatINR(subtotal)}</strong>
              </div>
              <button
                type="button"
                className="btn btn-primary rounded-pill store-cta cart-mobile-bar__btn"
                onClick={goToCheckout}
              >
                Checkout
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
